import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import logoFull from '../images/ThiranX Logo -.png';
import logoSymbol from '../images/ThiranX Logo Symbol.png';

/* ─── SVG ICONS ───────────────────────────────────────────── */
const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const PROMO_HEIGHT = 40;
const PROMO_ROUTES = ['/', '/about'];

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(() => sessionStorage.getItem('promo99_dismissed') !== '1');
  const promoVisible = showPromo && PROMO_ROUTES.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const handleLogout = () => { logout(); navigate('/'); };

  const dismissPromo = () => {
    setShowPromo(false);
    sessionStorage.setItem('promo99_dismissed', '1');
  };

  const navLinkStyle = {
    color: 'var(--muted)', fontWeight: 600, fontSize: 14.5,
    transition: 'color 0.2s', letterSpacing: '0.01em',
    fontFamily: "'Satoshi','DM Sans',sans-serif",
  };

  return (
    <>
      {promoVisible && (
        <div className="promo-bar" style={{ height: PROMO_HEIGHT }}>
          <div className="promo-bar-shine" />
          <div className="promo-bar-inner">
            <span className="promo-bar-dot" />
            <span className="promo-bar-icon">🎓</span>
            <span className="promo-bar-text">
              <strong>Real Micro-Internship + Certificate</strong> — Just ₹99
            </span>
            <Link to="/microinternships" className="promo-bar-cta">Enroll Now →</Link>
          </div>
          <button onClick={dismissPromo} aria-label="Dismiss" className="promo-bar-close">✕</button>
        </div>
      )}
      <nav style={{
        position: 'fixed', top: promoVisible ? PROMO_HEIGHT : 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? 'var(--nav-bg)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : 'none',
        transition: 'top 0.3s ease, background 0.3s ease',
      }}>
      <div className="nav-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, height: 70 }}>

        {/* Logo — pinned left */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src={logoFull}
            alt="ThiranX"
            className="nav-logo-full"
            style={{ height: 82, objectFit: 'contain' }}
          />
          <img
            src={logoSymbol}
            alt="ThiranX"
            className="nav-logo-symbol"
            style={{ height: 46, objectFit: 'contain', display: 'none' }}
          />
        </Link>

        {/* Desktop Nav Links — center, own breathing room from the logo and the auth cluster */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 26, minWidth: 0 }} className="desktop-nav-links">
          {[
            { to: '/about', label: 'About' },
            { to: '/courses', label: 'Courses' },
            { to: '/internships', label: 'Internships' },
            { to: '/microinternships', label: 'Microintern', badge: true },
            { to: '/dsa', label: 'DSA', badge: true },
            { to: '/services', label: 'Services', badge: true },
            { to: '/verify', label: 'Verify Certificate' },
            { to: '/contact', label: 'Contact' },
            { to: '/ai', label: 'ThiranX AI' },
          ].map(({ to, label, badge }) => (
            <Link key={to} to={to} className={to === '/ai' ? 'txbot-nav-link' : undefined} aria-current={location.pathname === to ? 'page' : undefined} style={{ ...navLinkStyle, position: 'relative', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
            >
              {label}
              {badge && (
                <span style={{
                  position: 'absolute', top: -8, right: -10,
                  background: 'linear-gradient(135deg,#7F00FF,#B163FF)',
                  color: '#fff', fontSize: 8.5, fontWeight: 800,
                  padding: '2px 5px', borderRadius: 4, letterSpacing: '0.04em',
                  lineHeight: 1,
                }}>NEW</span>
              )}
            </Link>
          ))}
        </div>

        {/* Auth + Theme Toggle — pinned right, visually separate from the nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }} className="desktop-nav-actions">
          {user ? (
            <>
              <Link
                to={user.role === 'admin' ? '/admin' : '/dashboard'}
                style={{ ...navLinkStyle, whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
              >
                {user.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
              </Link>
              <button onClick={handleLogout} className="btn btn-outline" style={{ padding: '8px 20px', fontSize: 14 }}>
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" style={{ ...navLinkStyle, whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
              >Login</Link>
              <Link to="/register" className="btn btn-primary" style={{ padding: '9px 22px', fontSize: 14, whiteSpace: 'nowrap' }}>
                Get Started
              </Link>
            </>
          )}

          {/* Theme Toggle */}
          <button
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{
              background: 'transparent',
              border: '1.5px solid var(--border-strong)',
              borderRadius: '50%',
              width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--v2)',
              transition: 'all 0.25s ease',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--v1)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = 'var(--v1)';
              e.currentTarget.style.boxShadow = '0 0 16px rgba(127,0,255,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--v2)';
              e.currentTarget.style.borderColor = 'var(--border-strong)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
        </div>

        {/* Mobile-only actions: theme icon + hamburger, grouped together */}
        <div className="mobile-actions" style={{ display: 'none', alignItems: 'center', gap: 10 }}>
          <button
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{
              background: 'none', border: '1.5px solid var(--border)', borderRadius: 10,
              cursor: 'pointer', color: 'var(--v2)',
              width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'var(--bg3)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'none'; }}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="hamburger"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="thiranx-mobile-navigation"
            style={{
              background: 'none', border: '1.5px solid var(--border)',
              borderRadius: 10, cursor: 'pointer', color: 'var(--text)',
              width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'var(--bg3)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'none'; }}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div id="thiranx-mobile-navigation" style={{
          background: 'var(--bg2)',
          borderTop: '1px solid var(--border)',
          padding: '20px 24px',
          maxHeight: `calc(100dvh - ${promoVisible ? 110 : 70}px)`, overflowY: 'auto', overscrollBehavior: 'contain',
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14,
          boxShadow: 'var(--shadow-lg)',
        }}>
          {[
            { to: '/about', label: 'About' },
            { to: '/courses', label: 'Courses' },
            { to: '/internships', label: 'Internships' },
            { to: '/microinternships', label: 'Microintern', badge: true },
            { to: '/dsa', label: 'DSA', badge: true },
            { to: '/services', label: 'Services', badge: true },
            { to: '/verify', label: 'Verify Certificate' },
            { to: '/contact', label: 'Contact' },
            { to: '/ai', label: 'ThiranX AI' },
          ].map(({ to, label, badge }) => (
            <Link key={to} to={to} style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
              {label}
              {badge && (
                <span style={{
                  background: 'linear-gradient(135deg,#7F00FF,#B163FF)',
                  color: '#fff', fontSize: 9, fontWeight: 800,
                  padding: '2px 6px', borderRadius: 4, letterSpacing: '0.04em',
                }}>NEW</span>
              )}
            </Link>
          ))}

          {user ? (
            <>
              <Link
                to={user.role === 'admin' ? '/admin' : '/dashboard'}
                style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15 }}
              >
                {user.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
              </Link>
              <button onClick={handleLogout} className="btn btn-outline" style={{ width: 'fit-content' }}>
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" style={{ color: 'var(--text)', fontWeight: 600, fontSize: 15 }}>Login</Link>
              <Link to="/register" className="btn btn-primary" style={{ width: 'fit-content' }}>Get Started</Link>
            </>
          )}
        </div>
      )}

      <style>{`
        .nav-logo-symbol { display: none !important; }
        .nav-container { max-width: 1600px; margin: 0 auto; padding: 0 40px; }
        @media (max-width: 1680px) { .nav-container { padding: 0 28px; } }
        @media (max-width: 1200px) { .nav-container { padding: 0 20px; } }

        /* ── Promo bar ── */
        @keyframes promoGradientMove { 0% { background-position: 0% 0%; } 100% { background-position: 200% 0%; } }
        @keyframes promoShine        { 0% { left: -60%; } 55% { left: 130%; } 100% { left: 130%; } }
        @keyframes promoDotPulse     { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.7); } }
        .promo-bar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1001;
          background: linear-gradient(90deg, var(--v1), var(--v2), var(--v1));
          background-size: 200% 100%; animation: promoGradientMove 6s linear infinite;
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .promo-bar-shine {
          position: absolute; top: 0; left: -60%; width: 40%; height: 100%;
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.28), transparent);
          transform: skewX(-20deg); animation: promoShine 4s ease-in-out infinite;
        }
        .promo-bar-inner { display: flex; align-items: center; gap: 9px; position: relative; z-index: 1; padding: 0 46px; }
        .promo-bar-dot { width: 6px; height: 6px; border-radius: 50%; background: #FFD166; flex-shrink: 0; animation: promoDotPulse 1.6s ease-in-out infinite; }
        .promo-bar-icon { font-size: 14px; }
        .promo-bar-text { color: #fff; font-size: 12.5px; font-weight: 600; font-family: 'Satoshi','DM Sans',sans-serif; white-space: nowrap; }
        .promo-bar-text strong { font-weight: 800; }
        .promo-bar-cta {
          background: #fff; color: var(--v1); font-size: 11.5px; font-weight: 800;
          padding: 5px 14px; border-radius: 999px; text-decoration: none; white-space: nowrap;
          transition: transform 0.15s ease;
        }
        .promo-bar-cta:hover { transform: translateY(-1px); }
        .promo-bar-close {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          background: rgba(255,255,255,0.15); border: none; color: #fff;
          width: 20px; height: 20px; border-radius: 50%; cursor: pointer; font-size: 10px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        @media (max-width: 480px) {
          .promo-bar-inner { padding: 0 34px; gap: 6px; }
          .promo-bar-text  { font-size: 11px; }
          .promo-bar-cta   { padding: 4px 10px; font-size: 10.5px; }
        }

        @media (max-width: 1400px) {
          .nav-container { padding: 0 16px; }
          .desktop-nav-links   { display: none !important; }
          .desktop-nav-actions { display: none !important; }
          .mobile-actions { display: flex !important; }
        }
        @media (max-width: 768px) {
          .nav-container { padding: 0 16px; }
          .nav-logo-full   { display: none !important; }
          .nav-logo-symbol { display: block !important; height: 44px !important; }
        }
        @media (max-width: 480px) {
          .nav-logo-symbol { height: 40px !important; }
        }
      `}</style>
      </nav>
    </>
  );
};

export default Navbar;
